import React, { useContext, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { Typography } from '@mui/material';
import {
    DOC_TYPE_DOWNLOADABLE,
    DOC_TYPE_INTERNAL,
    DOC_TYPE_REQUIRED,
    DossierJsonldRead,
} from '@europrocurement/l2d-domain';
import { castIriToId, getFileExtension } from '@europrocurement/l2d-utils';
import { useModal } from '@europrocurement/flexy-components';
import { useHandleError } from '@europrocurement/l2d-hooks';
import {
    type FproDossierDocJsonldDocumentfileRead,
    type TypeDeDocumentDesDossiersJsonldTypedocumentdossierRead,
    type FproDosDocFileJsonldDocumentfileRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DocumentContext, DocumentPreviewContext, useSelectedDossierContext } from '../providers';
import useDossierDocuments, { DocumentExtension, DossierFichier } from './useDossierDocuments';
import UpdateFileTypeDocModal from '../components/shared/UpdateFileTypeDocModal/UpdateFileTypeDocModal';
import { CreatedLiasseType, DisplayedDocument } from '../providers/types';
import useFileChecker from './useFileChecker';

type UseDossierFileProps = {
    listItems: DisplayedDocument[];
    setListItems: React.Dispatch<React.SetStateAction<DisplayedDocument[]>>;
    typeDoc: typeof DOC_TYPE_REQUIRED | typeof DOC_TYPE_INTERNAL | typeof DOC_TYPE_DOWNLOADABLE;
    setUploadingAdditionalDocument: React.Dispatch<React.SetStateAction<boolean>>;
    dossierProps?: DossierJsonldRead;
    source?: 'FRONT' | 'BACK';
};
const useDossierFile = ({
    listItems,
    setListItems,
    typeDoc,
    setUploadingAdditionalDocument,
    dossierProps,
    source = 'FRONT',
}: UseDossierFileProps) => {
    const {
        documents,
        fichiers,
        setFichiers,
        setDocuments,
        addFichier,
        addDocument,
        replaceFichier,
        removeFichier,
        addMultipleFichiers,
        refreshDocumentContext,
    } = useContext(DocumentContext);
    const numberFilesChanged = useRef(0);
    const { enqueueSnackbar } = useSnackbar();
    const { setDocumentPreview, documentPreview } = useContext(DocumentPreviewContext);
    const { dossier } = useSelectedDossierContext();
    const { modalActions } = useModal();
    const { apiErrorMessageSnackbar } = useHandleError();
    const {
        getFichierFromDocument,
        uploadDossierFichier,
        removeDossierFichier,
        removeDossierDocument,
        downloadFichier,
        getFichierContent,
        isFileSupported,
        uploadAdditionalDossierFichier,
        updateDossierFichier,
    } = useDossierDocuments();
    const { displayFilesCheckerForm } = useFileChecker();

    const updateListItem = (query: Record<string, number>, payload: object) => {
        const items = listItems.map((item) => {
            const match = Object.keys(query).every(
                (key: 'id' | 'fichierId') => item[key] === query[key],
            );

            if (match) {
                return { ...item, ...payload };
            }

            return { ...item };
        });

        setListItems(items);
    };

    const handleFilePreview = async ({
        fichier,
        document,
        dossierId,
    }: {
        fichier: DossierFichier;
        document:
            | TypeDeDocumentDesDossiersJsonldTypedocumentdossierRead
            | FproDossierDocJsonldDocumentfileRead;
        dossierId?: number;
    }) => {
        const file = await getFichierContent(fichier.id as number);
        // Dossier from props, document or from selectedDossier
        let fdosId = dossierId;
        if (!fdosId) {
            fdosId = castIriToId(
                'dossier' in document && document.dossier
                    ? (document.dossier as string)
                    : (dossier['@id'] as string),
            );
        }
        const statusId = fichier?.statut?.id
            ? fichier?.statut?.id
            : castIriToId(document.statut as string);
        const fileName = fichier.fichier as string;
        const ext = getFileExtension(fileName);

        const newDocumentPreview = {
            fileName,
            fileType: ext as DocumentExtension,
            dossierId: fdosId,
            fichierId: fichier?.id as number,
            statusId,
            type: document?.document?.type as number,
            expediteurType: fichier?.expediteur ? fichier?.expediteur : typeDoc,
            documentLabel: document.libelle as string,
            documentTypeId: document.id,
            content: file.data as unknown as Blob,
        };

        if (!isFileSupported(newDocumentPreview.fileType as DocumentExtension)) {
            const fichierSearchSupport = fichiers.find(
                (f: DossierFichier) => Number(f.id) === fichier.id,
            );

            if (fichierSearchSupport) {
                await downloadFichier(fichierSearchSupport);
            }
        }

        setDocumentPreview(newDocumentPreview);
    };

    const handleAddFile = async (
        dossierId: number,
        documentId: number,
        file: File,
        fichierId?: number,
    ) => {
        const document = documents.find((doc) => Number(doc.id) === documentId);
        const isProcessedByIaDoc = true;
        let deliassageAuto = true;

        if (!document) {
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, { variant: 'error' });

            return;
        }

        const query: Record<string, number> =
            fichierId !== undefined ? { fichierId } : { id: documentId };
        updateListItem(query, { uploading: true, uploadable: false });
        let existingFichier = null;

        if (fichierId) {
            existingFichier = fichiers.find((f: DossierFichier) => Number(f.id) === fichierId);
        }

        if (undefined === existingFichier) {
            existingFichier = getFichierFromDocument(document, fichiers);
        }

        if (existingFichier) {
            removeDossierFichier(existingFichier.id as number);
        }

        if (source === 'FRONT') {
            deliassageAuto = false;
        }
        try {
            const fichiersRes = await uploadDossierFichier(
                dossierId,
                `/formalite/types-documents/${document?.document?.id}` as string,
                file,
                typeDoc,
                undefined,
                isProcessedByIaDoc,
                deliassageAuto,
            );
            if (fichiersRes.files && fichiersRes.files.length > 0) {
                if (fichiersRes.files.length === 1) {
                    enqueueSnackbar(<Typography>Votre fichier a bien été ajouté</Typography>, {
                        variant: 'success',
                    });
                    const newFile = fichiersRes.files[0];
                    if (existingFichier) {
                        replaceFichier(existingFichier, newFile);
                    } else {
                        addFichier(newFile);
                        if (newFile.typeDocDossier?.id) {
                            addDocument({
                                ...newFile.typeDocDossier,
                                dossier: newFile.dossier,
                                statut: newFile.statut?.['@id'],
                            });

                            // Display document preview on single file upload
                            await handleFilePreview({
                                fichier: newFile,
                                document: newFile.typeDocDossier,
                                dossierId,
                            });
                        }
                    }
                    numberFilesChanged.current += 1;
                } else {
                    // Vérifier si le type de doc courant correpond à un des résultats envoyés,
                    // dans ce cas on vient mettre a jour le fichier
                    fichiersRes.files.forEach(() => {
                        numberFilesChanged.current += 1;
                    });
                    enqueueSnackbar(<Typography>Vos fichiers ont bien été ajoutés</Typography>, {
                        variant: 'success',
                    });

                    displayFilesCheckerForm(
                        [{ originalFile: file, createdFiles: fichiersRes.files }],
                        getFichierContent,
                    );
                    refreshDocumentContext();
                }
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                <Typography>Une erreur est survenue lors du transfert de votre fichier</Typography>,
                {
                    variant: 'error',
                },
            );
            updateListItem(query, { uploading: false });
        }
    };

    const handleRemoveFile = async (fichierId: number) => {
        const fichier = fichiers.find((f: DossierFichier) => Number(f.id) === fichierId);

        if (!fichier) {
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, {
                variant: 'error',
            });

            return;
        }

        updateListItem({ fichierId }, { uploading: true });
        try {
            await removeDossierFichier(fichierId);
            removeFichier(fichier);

            // Close document preview if the current file is deleted
            if (documentPreview && documentPreview.fichierId === fichierId) {
                setDocumentPreview(undefined);
            }

            enqueueSnackbar(<Typography>Votre fichier a bien été supprimé</Typography>, {
                variant: 'success',
            });
        } catch (error) {
            console.error(error);
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, {
                variant: 'error',
            });
        }
    };

    const handleShow = async (label: string, fichierId: number, documentId: number) => {
        updateListItem({ fichierId }, { uploading: true });

        const fichier = fichiers.find((f: DossierFichier) => Number(f.id) === fichierId);

        if (!fichier) {
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, { variant: 'error' });

            return;
        }

        try {
            const document = documents.find((doc) => Number(doc.id) === documentId);

            if (!document) {
                throw new Error('No document found');
            }

            const fichierSearch = fichiers.find(
                (f: DossierFichier) =>
                    Number(f.id) === fichierId && Number(f.typeDocDossier?.id) === documentId,
            );

            if (!fichierSearch) {
                throw new Error('No file found');
            }

            await handleFilePreview({ fichier, document });
        } catch (err) {
            console.log(err);
            enqueueSnackbar(`Une erreur est survenue lors de l'affichage du document ${label}`, {
                variant: 'error',
            });
        } finally {
            updateListItem({ fichierId }, { uploading: false });
        }
    };

    const handleDownload = async (fichierId: number) => {
        const fichier = fichiers.find((f: DossierFichier) => Number(f.id) === fichierId);

        if (!fichier) {
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, { variant: 'error' });

            return;
        }

        try {
            await downloadFichier(fichier);
        } catch (err) {
            console.error(err);
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, { variant: 'error' });
        }
    };

    const handleAdditionalUpload = async (file: File) => {
        let typageAuto = true;
        let deliassageAuto = false;
        if (typeDoc === DOC_TYPE_INTERNAL && file.type === '') {
            typageAuto = false;
        }
        if (source === 'FRONT') {
            deliassageAuto = false;
        }

        const dossierId = dossier?.id ? dossier.id : dossierProps?.id;

        if (!dossierId) {
            enqueueSnackbar(
                <Typography>Une erreur est survenue lors du transfert de votre fichier</Typography>,
                {
                    variant: 'error',
                },
            );
            throw new Error('Dossier manquant');
        }

        const fichier = await uploadAdditionalDossierFichier(
            dossierId as number,
            file,
            typeDoc,
            5,
            typageAuto,
            deliassageAuto,
        );
        numberFilesChanged.current += 1;
        return fichier;
    };

    const handleAdditionalUploads = async (files: FileList) => {
        setUploadingAdditionalDocument(true);

        let filesResponse: FproDosDocFileJsonldDocumentfileRead[][] = [];
        try {
            await Promise.all(Array.from(files).map((file) => handleAdditionalUpload(file))).then(
                (newFichiers) => {
                    filesResponse = newFichiers
                        .filter((fichierPost) => fichierPost.files !== undefined)
                        .map(
                            (fichierPost) =>
                                fichierPost.files as FproDosDocFileJsonldDocumentfileRead[],
                        );

                    const flattenFilesReponse = filesResponse.flat();
                    // Update fichiers context
                    addMultipleFichiers(flattenFilesReponse);
                    // Update documents context
                    flattenFilesReponse.map((fileReponse) =>
                        addDocument({
                            ...fileReponse.typeDocDossier,
                            dossier: fileReponse.dossier,
                            statut: fileReponse.statut?.['@id'],
                        }),
                    );
                    enqueueSnackbar(<Typography>Vos fichiers ont bien été ajoutés</Typography>, {
                        variant: 'success',
                    });
                },
            );
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                <Typography>Une erreur est survenue lors du transfert de vos fichiers</Typography>,
                {
                    variant: 'error',
                },
            );
        } finally {
            if (filesResponse) {
                // check forEach fileResponse if multiple files
                const arrayOfLiasse: CreatedLiasseType[] = [];
                // check if multiples files have been generated of an entry and push the object of original and created files
                filesResponse.forEach((createdFiles, index) => {
                    if (createdFiles.length > 1) {
                        arrayOfLiasse.push({
                            originalFile: files[index],
                            createdFiles,
                        });
                    }
                });
                if (arrayOfLiasse.length > 0) {
                    // files checker modal
                    displayFilesCheckerForm(arrayOfLiasse, getFichierContent);
                }
            }

            setUploadingAdditionalDocument(false);
        }
    };

    const updateFileTypeDoc = (
        fichierId: number,
        typeDocId:
            | typeof DOC_TYPE_REQUIRED
            | typeof DOC_TYPE_INTERNAL
            | typeof DOC_TYPE_DOWNLOADABLE,
    ) => {
        updateDossierFichier(fichierId, { expediteur: typeDocId })
            .then((res) => {
                enqueueSnackbar(
                    <Typography>Le type de document du fichier a bien été modifié</Typography>,
                );
                if (res.data.files && res.data.files.length > 0) {
                    setFichiers((prevState) => {
                        const newState = [...prevState];
                        res.data.files!.forEach((file) => {
                            // find and update current file
                            const index = newState.findIndex((el) => el.id === file.id);
                            if (index !== -1) {
                                newState[index] = { ...newState[index], ...file };
                            } else {
                                // add new files if created from liasse
                                newState.push(file);
                            }
                        });
                        return newState;
                    });
                }
            })
            .catch((err) =>
                apiErrorMessageSnackbar(
                    err,
                    'Une erreur est survenue lors de la modification du type de document',
                ),
            )
            .finally(() => modalActions.close());
    };

    // soft delete dosDoc and remove document from context
    const handleRemoveDocument = async (documentId: number) => {
        try {
            const result = await removeDossierDocument(documentId);
            if ([200, 201, 204].includes(result.status)) {
                // Remove document from the context
                setDocuments(documents.filter((doc) => doc.id !== documentId));
            } else {
                throw new Error(
                    'Une erreur est survenue lors de la modification du type de document',
                );
            }
        } catch (error) {
            console.error(error);
            enqueueSnackbar(
                <Typography>Une erreur est survenue lors de la suppression du document</Typography>,
                {
                    variant: 'error',
                },
            );
            throw new Error('Une erreur est survenue lors de la modification du type de document');
        }
    };

    const handleMoveFile = (label: string, fichierId: number) => {
        modalActions.call(
            <UpdateFileTypeDocModal
                label={label}
                fichierId={fichierId}
                typeDoc={typeDoc}
                actionOnValidation={updateFileTypeDoc}
            />,
        );
    };

    return {
        handleAddFile,
        handleRemoveFile,
        handleRemoveDocument,
        handleShow,
        handleDownload,
        handleAdditionalUploads,
        handleMoveFile,
        numberFilesChanged,
    };
};

export default useDossierFile;
